import React from 'react';
import Layout from '../components/Layout'; // Importa o Layout
import { PageProps } from '../App'; // Importa a interface PageProps

// Importar logos dos parceiros
import finepLogo from '../assets/finep-logo.jpg';
import prodespLogo from '../assets/prodesp-logo.png';
import clouderaLogo from '../assets/cloudera-logo.png';
import ibmLogo from '../assets/ibm-logo.png';
import suseLogo from '../assets/suse-logo.png';
import mongodbLogo from '../assets/mongodb-logo.jpg';
import awsLogo from '../assets/aws-logo.png';

// Adiciona PageProps à definição do componente
const ParceirosPage: React.FC<PageProps> = ({ darkMode, toggleDarkMode }) => {
  return (
    // Passa as props para o Layout
    <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
      <div className="bg-white dark:bg-gray-900">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-lupa-orange to-lupa-yellow overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-lupa-purple-dark/70 to-lupa-purple/30 z-10"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-20">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight font-heading">
                Parceiros
              </h1>
              <p className="mt-6 text-xl text-white/90 max-w-3xl mx-auto">
                Construindo o futuro dos dados através de parcerias estratégicas e colaborações inovadoras.
              </p>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full h-auto">
              <path fill="#ffffff" fillOpacity="1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,149.3C960,160,1056,160,1152,138.7C1248,117,1344,75,1392,53.3L1440,32L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            </svg>
          </div>
        </div>

        {/* Conteúdo Principal */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-lupa-purple dark:text-white mb-4">Parcerias Estratégicas</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-4xl mx-auto">
              Na LupaData, temos vasto conhecimento em tecnologias open source e somos abertos a trabalhar com todas as tecnologias. 
              No mercado, reconhecemos a importância estratégica de estabelecer parcerias sólidas com organizações que compartilham 
              nossa visão de inovação e excelência tecnológica. Essas colaborações nos permitem oferecer soluções mais robustas 
              e abrangentes aos nossos clientes, combinando expertise e recursos para enfrentar os desafios mais complexos do mundo dos dados.
            </p>
          </div>

          {/* Parceiros Institucionais */}
          <div className="mb-20">
            <h2 className="text-2xl font-bold text-lupa-purple dark:text-white mb-8 text-center">Parceiros Institucionais</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
              {/* FINEP */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
                <div className="flex justify-center mb-6">
                  <img 
                    src={finepLogo} 
                    alt="FINEP - Financiadora de Estudos e Projetos" 
                    className="h-20 object-contain"
                  />
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-4">FINEP</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Financiadora de Estudos e Projetos, empresa pública vinculada ao Ministério da Ciência, 
                  Tecnologia e Inovações, que promove o desenvolvimento econômico e social do Brasil por meio 
                  do fomento público à ciência, tecnologia e inovação.
                </p>
                <a 
                  href="http://www.finep.gov.br/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-lupa-orange font-medium hover:underline"
                >
                  Visitar site
                  <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>

              {/* PRODESP */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
                <div className="flex justify-center mb-6">
                  <img 
                    src={prodespLogo} 
                    alt="PRODESP - Companhia de Processamento de Dados do Estado de São Paulo" 
                    className="h-20 object-contain"
                  />
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-4">PRODESP</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Companhia de Processamento de Dados do Estado de São Paulo, responsável pela gestão 
                  de tecnologia da informação do governo paulista e pelo fomento ao ecossistema de 
                  startups e inovação tecnológica no estado.
                </p>
                <a 
                  href="https://www.prodesp.sp.gov.br/startups" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-lupa-orange font-medium hover:underline"
                >
                  Visitar site
                  <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Parceiros Tecnológicos */}
          <div className="mb-20">
            <h2 className="text-2xl font-bold text-lupa-purple dark:text-white mb-8 text-center">Parceiros Tecnológicos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Cloudera */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
                <div className="flex justify-center mb-4">
                  <img 
                    src={clouderaLogo} 
                    alt="Cloudera" 
                    className="h-16 object-contain"
                  />
                </div>
                <h3 className="text-lg font-bold text-lupa-purple dark:text-white mb-2">Cloudera</h3>
                <p className="text-gray-700 dark:text-gray-300 text-sm">
                  Plataforma líder em dados empresariais, oferecendo soluções completas para analytics, 
                  machine learning e gestão de dados em escala.
                </p>
              </div>

              {/* IBM */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
                <div className="flex justify-center mb-4">
                  <img 
                    src={ibmLogo} 
                    alt="IBM" 
                    className="h-16 object-contain"
                  />
                </div>
                <h3 className="text-lg font-bold text-lupa-purple dark:text-white mb-2">IBM</h3>
                <p className="text-gray-700 dark:text-gray-300 text-sm">
                  Líder global em tecnologia empresarial, oferecendo soluções avançadas em IA, 
                  cloud computing e transformação digital.
                </p>
              </div>

              {/* SUSE */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
                <div className="flex justify-center mb-4">
                  <img 
                    src={suseLogo} 
                    alt="SUSE" 
                    className="h-16 object-contain"
                  />
                </div>
                <h3 className="text-lg font-bold text-lupa-purple dark:text-white mb-2">SUSE</h3>
                <p className="text-gray-700 dark:text-gray-300 text-sm">
                  Pioneira em soluções Linux empresariais e tecnologias open source, 
                  fornecendo infraestrutura confiável para ambientes críticos.
                </p>
              </div>

              {/* MongoDB */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
                <div className="flex justify-center mb-4">
                  <img 
                    src={mongodbLogo} 
                    alt="MongoDB" 
                    className="h-16 object-contain"
                  />
                </div>
                <h3 className="text-lg font-bold text-lupa-purple dark:text-white mb-2">MongoDB</h3>
                <p className="text-gray-700 dark:text-gray-300 text-sm">
                  Plataforma de banco de dados NoSQL líder de mercado, oferecendo flexibilidade 
                  e escalabilidade para aplicações modernas.
                </p>
              </div>

              {/* AWS */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
                <div className="flex justify-center mb-4">
                  <img 
                    src={awsLogo} 
                    alt="Amazon Web Services" 
                    className="h-16 object-contain"
                  />
                </div>
                <h3 className="text-lg font-bold text-lupa-purple dark:text-white mb-2">Amazon Web Services</h3>
                <p className="text-gray-700 dark:text-gray-300 text-sm">
                  Plataforma de cloud computing mais abrangente do mundo, oferecendo 
                  serviços de infraestrutura, analytics e machine learning.
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="bg-gradient-to-r from-lupa-orange to-lupa-yellow rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Interessado em uma Parceria?</h2>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Estamos sempre abertos a novas colaborações estratégicas. Se sua organização compartilha 
              nossa paixão por inovação em dados e tecnologia, vamos conversar sobre as possibilidades.
            </p>
            <a 
              href="/contato" 
              className="inline-block bg-white text-lupa-orange font-bold py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Entre em Contato
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ParceirosPage;
